import { useParams } from 'react-router-dom'
import {
  useGetProjectMainSettingsQuery,
  usePostProjectUpdateTermsMutation,
} from '../../../../app/api'
import { BpTextArea } from '../../../../shared/components/forms/textarea/BpTextArea'
import { BpButton } from '../../../../shared/components/buttons/bp-button/BpButton'
import React, { useEffect, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../../app/store/hooks/Redux'
import {
  selectProjectTerms,
  selectProjectTermsChanged,
  setProjectTerms,
  updateProjectTerm,
} from '../../../projects/model/projectsSlice'
import { ITerm } from '../../../projects/model/types/Term.interface'
import { useTranslation } from 'react-i18next'
import { setModal } from '../../../../app/store/reducers/AppSlice'
import { selectTariffDetails, setTariffUpdated } from '../../../../app/store/reducers/AuthSlice'
import { CardDrop } from '../../../../shared/components/cards/card-drop/CardDrop'
import { Preloader } from '../../../../shared/components/cards/preloader/Preloader'
import { AiButton } from '../../../../shared/components/ai/ai-button/AiButton'
import { useAiChat } from '../../../../shared/components/ai/ai-provider/AiProvider'
import { TariffDetailsModal } from '../../../projects/widgets/tariff-details-modal/TariffDetailsModal'
import styles from './ProjectTerms.module.scss'
import { Notify } from '../../../../shared/components/text/notify/Notify'

export const ProjectTerms = ({ data, loading: updateTermsLoading }: any) => {
  const { t } = useTranslation()
  const { id } = useParams()
  const { handleSendMessage } = useAiChat()
  const tariffDetails = useAppSelector(selectTariffDetails)
  const [limitExceeded, setLimitExceeded] = useState<boolean>(false)
  const termsList = useAppSelector(selectProjectTerms)
  const termsChanged = useAppSelector(selectProjectTermsChanged)
  const dispatch = useAppDispatch()
  const [loading, setLoading] = useState(false)

  const { data: mainSettings } = useGetProjectMainSettingsQuery(
    { id: Number(id) },
    {
      selectFromResult: (result) => {
        const item = result.data?.data?.items?.[0]

        return {
          ...result,
          data: {
            ...item,
          },
        }
      },
      skip: !id,
    },
  )

  const [updateTerms, { isLoading }] = usePostProjectUpdateTermsMutation()

  const filteredTermsList = useMemo(
    () =>
      termsList?.map((term) => ({
        ...term,
        terms: term.terms.filter((item) => item !== ''),
      })),
    [termsList],
  )

  const emptyTerms = termsList?.filter((item) => !item.terms?.length)

  const counter = useMemo(() => {
    const list = data?.data?.items || []
    let count = tariffDetails?.tariff?.used_keywords_count || 0

    if (filteredTermsList) {
      for (const term of list) {
        count -= term.terms.length
      }

      for (const term of filteredTermsList) {
        count += term.terms.length
      }
    }

    return count
  }, [filteredTermsList, data])

  const submit = () => {
    setLoading(true)

    updateTerms({ body: { project_id: Number(id), data: filteredTermsList } }).then((response) => {
      dispatch(setTariffUpdated())
      setTimeout(() => setLoading(false), 500)

      if (
        !('error' in response) &&
        (response.data as any).errors.flat().includes('Tariff keywords limit exceeded.')
      ) {
        setLimitExceeded(true)
      } else {
        setLimitExceeded(false)
      }
    })
  }

  const cancel = () => {
    dispatch(setProjectTerms(data?.data.items))
  }

  useEffect(() => {
    if (updateTermsLoading) {
      setLoading(true)
    } else {
      setTimeout(() => setLoading(false), 500)
    }
  }, [updateTermsLoading])

  if (isLoading || loading) {
    return (
      <div className={styles.preloaderWrapper}>
        <Preloader />
      </div>
    )
  }

  return (
    <div className={styles.root}>
      <div className={styles.topRow}>
        {!!emptyTerms?.length && (
          <Notify>
            Missing keywords for {emptyTerms.length} locations. To start collecting data add
            keywords first.
          </Notify>
        )}
        <p className={styles.description}>{t('pages.project.termsDescription')}</p>
      </div>
      <div className={styles.termsGrid}>
        {termsList?.map((term: ITerm, index: number) => (
          <div key={index} className={styles.termItem}>
            <BpTextArea
              action={
                <button
                  className={styles.clearButton}
                  type={'button'}
                  onClick={() => {
                    dispatch(updateProjectTerm({ ...term, terms: [] }))
                  }}
                >
                  {t('buttons.clearList')}
                </button>
              }
              rightSection={
                <AiButton
                  size={'sm'}
                  variant={'outlined'}
                  onClick={() =>
                    handleSendMessage({
                      message: `Generate keywords for my brand ${mainSettings?.brand_name}, Country ${term.country_name}, Language ${term.language_name}`,
                    })
                  }
                >
                  {t('buttons.createWithAI')}
                </AiButton>
              }
              error={!term.terms.length}
              label={`${term.country_name}, ${term.language_name}: ${term.terms.filter((term: string) => !!term.trim().length).length}`}
              placeholder={t('pages.project.placeholders.terms')}
              value={term.terms.join('\n')}
              onBlur={(e) => {
                dispatch(
                  updateProjectTerm({
                    ...term,
                    terms: e.target.value
                      .replace(',', '\n')
                      .split('\n')
                      .filter((term: string) => !!term.trim().length),
                  }),
                )
              }}
              onChange={(e) => {
                const value = e.target.value.replace(',', '\n').split('\n')

                if (value.find((string: string) => string.length > 50)) {
                  return
                }

                dispatch(
                  updateProjectTerm({
                    ...term,
                    terms: value,
                  }),
                )
              }}
            />
          </div>
        ))}
      </div>
      {limitExceeded ||
        (tariffDetails?.tariff && counter > tariffDetails.tariff.max_keywords && (
          <div className={styles.limitBanner}>
            <p className={styles.limitBannerText}>
              {t('pages.tariff.limitExceeded', { tariff: tariffDetails?.tariff?.name })}
            </p>
            <div className={styles.limitBannerActions}>
              <BpButton
                contentClassName={'!text-[#00326D] !font-normal'}
                size={'sm'}
                theme={'light'}
                type={'button'}
                onClick={() => dispatch(setModal({ children: <TariffDetailsModal /> }))}
              >
                {t('pages.tariff.buttons.changePlan')}
              </BpButton>
              <CardDrop title={t('pages.tariff.buttons.details')}>
                <div className={styles.cardDropContent}>
                  <div className={styles.cardDropRow}>
                    <p
                      dangerouslySetInnerHTML={{
                        __html: t('pages.tariff.usedCountOfTotalKeywords', {
                          count: tariffDetails?.tariff?.used_keywords_count,
                          total: tariffDetails?.tariff?.max_keywords,
                        }),
                      }}
                      className={styles.cardDropText}
                    />
                  </div>
                  <ul className={styles.cardDropList}>
                    {Object.entries(tariffDetails?.location_details).map(([key, value]) => (
                      <li key={value}>
                        {key}: {value}
                      </li>
                    ))}
                  </ul>
                </div>
              </CardDrop>
            </div>
          </div>
        ))}
      <div className={styles.footer}>
        <BpButton
          disabled={
            !termsChanged ||
            !!(tariffDetails?.tariff && counter > tariffDetails.tariff.max_keywords)
          }
          className={styles.footerButton}
          loading={isLoading}
          onClick={submit}
        >
          {t('buttons.saveChanges')}
        </BpButton>
        <BpButton disabled={!termsChanged} theme='transparent' onClick={cancel}>
          {t('buttons.cancel')}
        </BpButton>
      </div>
    </div>
  )
}
